import { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  Typography,
  Alert,
  Snackbar,
  Stack,
  InputAdornment,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Switch,
  FormControlLabel,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { Worker, WorkerFormData } from '../types/worker';
import SearchIcon from '@mui/icons-material/Search';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import VisibilityIcon from '@mui/icons-material/Visibility';
import api, { workerApi } from '../services/api';

const initialFormData: WorkerFormData = {
  worker_id: '',
  name: '',
  status: '',
  joining_date: new Date().toISOString().split('T')[0], // Set default date to today in yyyy-MM-dd format
  adhaar_no: '',
  pan: '',
  bank_account_name: '',
  bank_account_no: '',
  ifsc: '',
  worker_type: 'STITCHING', // Add default worker type
  phone: '',
  address: '',
  rate: undefined,
};

export default function Workers() {
  const [workers, setWorkers] = useState<Worker[]>([]);
  const [filteredWorkers, setFilteredWorkers] = useState<Worker[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState<WorkerFormData>(initialFormData);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [editMode, setEditMode] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [workerToDelete, setWorkerToDelete] = useState<number | null>(null);
  const [selectedWorkerId, setSelectedWorkerId] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showMissingDocs, setShowMissingDocs] = useState(false);

  const columns: GridColDef[] = [
    { field: 'worker_id', headerName: 'Worker ID', width: 130 },
    { field: 'name', headerName: 'Name', width: 200 },
    { field: 'status', headerName: 'Status', width: 130 },
    { field: 'worker_type', headerName: 'Worker Type', width: 130 },
    { 
      field: 'joining_date', 
      headerName: 'Joining Date', 
      width: 150,
      valueFormatter: (params: any) => {
        // Format date as yyyy-MM-dd
        if (!params || params.value === undefined || params.value === null) return '';
        try {
          const date = new Date(params.value);
          // Check if date is valid
          if (isNaN(date.getTime())) return '';
          return date.toISOString().split('T')[0];
        } catch (error) {
          console.error('Error formatting date:', error);
          return '';
        }
      }
    },
    { 
      field: 'adhaar_no', 
      headerName: 'Aadhaar No', 
      width: 200,
      renderCell: (params) => (
        <Stack direction="row" spacing={1} alignItems="center">
          <span>{params.value}</span>
          <IconButton
            size="small"
            onClick={() => handleDocumentUpload('adhaar_doc', params.row.id)}
          >
            <FileUploadIcon fontSize="small" />
          </IconButton>
          {params.row.adhaar_doc && (
            <IconButton
              size="small"
              onClick={() => handleViewDocument('adhaar_doc', params.row.id)}
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
          )}
        </Stack>
      )
    },
    { 
      field: 'pan', 
      headerName: 'PAN', 
      width: 180,
      renderCell: (params) => (
        <Stack direction="row" spacing={1} alignItems="center">
          <span>{params.value}</span>
          <IconButton
            size="small"
            onClick={() => handleDocumentUpload('pan_doc', params.row.id)}
          >
            <FileUploadIcon fontSize="small" />
          </IconButton>
          {params.row.pan_doc && (
            <IconButton
              size="small"
              onClick={() => handleViewDocument('pan_doc', params.row.id)}
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
          )}
        </Stack>
      )
    },
    { field: 'bank_account_name', headerName: 'Bank Account Name', width: 200 },
    { 
      field: 'bank_account_no', 
      headerName: 'Bank Account No', 
      width: 200,
      renderCell: (params) => (
        <Stack direction="row" spacing={1} alignItems="center">
          <span>{params.value}</span>
          <IconButton
            size="small"
            onClick={() => handleDocumentUpload('bank_doc', params.row.id)}
          >
            <FileUploadIcon fontSize="small" />
          </IconButton>
          {params.row.bank_doc && (
            <IconButton
              size="small"
              onClick={() => handleViewDocument('bank_doc', params.row.id)}
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
          )}
        </Stack>
      )
    },
    { field: 'ifsc', headerName: 'IFSC', width: 130 },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 200,
      renderCell: (params) => (
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            color="primary"
            onClick={() => handleEdit(params.row)}
          >
            Edit
          </Button>
          <Button
            variant="outlined"
            color="error"
            onClick={() => handleDeleteClick(params.row.id)}
          >
            Delete
          </Button>
        </Stack>
      ),
    },
  ];

  const fetchWorkers = async () => {
    try {
      const data = await workerApi.getAll();
      setWorkers(data);
      setFilteredWorkers(data);
    } catch (error: any) {
      console.error('Error fetching workers:', error);
      setError(error.response?.data?.error || 'Failed to fetch workers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWorkers();
  }, []);

  // Filter workers based on search query and missing documents
  useEffect(() => {
    let filtered = workers;

    // First apply missing documents filter if enabled
    if (showMissingDocs) {
      filtered = workers.filter(worker => 
        !worker.adhaar_doc || !worker.pan_doc || !worker.bank_doc
      );
    }

    // Then apply search query filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(worker => 
        worker.worker_id.toLowerCase().includes(query) ||
        worker.name.toLowerCase().includes(query) ||
        worker.status.toLowerCase().includes(query) ||
        worker.adhaar_no.toLowerCase().includes(query) ||
        worker.pan.toLowerCase().includes(query) ||
        worker.bank_account_name.toLowerCase().includes(query) ||
        worker.bank_account_no.toLowerCase().includes(query) ||
        worker.ifsc.toLowerCase().includes(query) ||
        worker.joining_date.toString().includes(query)
      );
    }

    setFilteredWorkers(filtered);
  }, [searchQuery, workers, showMissingDocs]);

  const handleOpen = () => {
    setEditMode(false);
    setFormData(initialFormData);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setFormData(initialFormData);
    setError(null);
    setEditMode(false);
    setSelectedWorkerId(null);
  };

  const handleEdit = (worker: Worker) => {
    setEditMode(true);
    setSelectedWorkerId(worker.id);
    
    // Safe date conversion
    let formattedDate = "";
    try {
      // Try to convert to ISO and get YYYY-MM-DD part
      const date = new Date(worker.joining_date);
      if (!isNaN(date.getTime())) {
        formattedDate = date.toISOString().split('T')[0];
      } else {
        // Fallback to today's date if the provided date is invalid
        formattedDate = new Date().toISOString().split('T')[0];
        console.error('Invalid date value in worker record:', worker.joining_date);
      }
    } catch (error) {
      console.error('Error formatting joining date:', error);
      // Fallback to today's date if there's an error
      formattedDate = new Date().toISOString().split('T')[0];
    }
    
    setFormData({
      worker_id: worker.worker_id,
      name: worker.name,
      status: worker.status,
      joining_date: formattedDate,
      adhaar_no: worker.adhaar_no,
      pan: worker.pan,
      bank_account_name: worker.bank_account_name,
      bank_account_no: worker.bank_account_no,
      ifsc: worker.ifsc,
      worker_type: worker.worker_type,
      phone: worker.phone,
      address: worker.address,
      rate: worker.rate ? String(worker.rate) : undefined,
    });
    setOpen(true);
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async () => {
    try {
      // Define truly required fields (rate is optional)
      const requiredFields = [
        'worker_id',
        'name',
        'status',
        'joining_date',
        'worker_type',
        'phone',
        'address',
      ];

      // Check required fields
      const missingFields = requiredFields.filter(field => !formData[field as keyof WorkerFormData]);
      if (missingFields.length > 0) {
        const missingFieldNames = missingFields.map(field => 
          field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
        );
        setError(`Please fill in required fields: ${missingFieldNames.join(', ')}`);
        return;
      }

      // Validate joining date
      try {
        const date = new Date(formData.joining_date);
        if (isNaN(date.getTime())) {
          setError('Please enter a valid joining date');
          return;
        }
      } catch (error) {
        setError('Invalid date format');
        return;
      }

      // Create data object with validated data
      const dataToSubmit = { ...formData };

      console.log('Submitting worker data:', dataToSubmit);

      if (editMode && selectedWorkerId) {
        await workerApi.update(selectedWorkerId, dataToSubmit);
        console.log('Worker updated successfully');
      } else {
        await workerApi.create(dataToSubmit);
        console.log('Worker created successfully');
      }

      handleClose();
      await fetchWorkers();
      setError(null);
    } catch (error: any) {
      console.error('Error saving worker:', error);
      console.error('Response data:', error.response?.data);
      console.error('Status code:', error.response?.status);
      setError(error.response?.data?.error || error.message || 'Failed to save worker');
    }
  };

  const handleDeleteClick = (id: number) => {
    setWorkerToDelete(id);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (workerToDelete) {
      try {
        console.log(`Attempting to delete worker with ID: ${workerToDelete}`);
        
        const response = await workerApi.delete(workerToDelete);
        console.log('Delete API response:', response);
        
        // Add delay before refreshing the list
        setTimeout(async () => {
          await fetchWorkers();
          setDeleteConfirmOpen(false);
          setWorkerToDelete(null);
        }, 500);
      } catch (error: any) {
        console.error('Error deleting worker:', error);
        console.error('Response data:', error.response?.data);
        console.error('Status code:', error.response?.status);
        setError(error.response?.data?.error || `Failed to delete worker (${error.message})`);
      }
    }
  };

  const handleDocumentUpload = async (docType: 'adhaar_doc' | 'pan_doc' | 'bank_doc', workerId: number) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'application/pdf,image/*';
    
    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return;

      try {
        setLoading(true);
        setError(null);
        setSuccessMessage(null);

        // First, upload file to Google Cloud Storage
        const uploadFormData = new FormData();
        uploadFormData.append('file', file);

        // Upload to Cloud Storage via the general upload endpoint
        const uploadResponse = await api.post('/upload', uploadFormData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });

        if (!uploadResponse.data.url) {
          throw new Error('No URL returned from server');
        }

        // Now update the worker record with the document URL
        const documentUrl = uploadResponse.data.url;
        
        // Create update payload with the document URL
        const updateData: any = {};
        updateData[docType] = documentUrl;

        // Update worker record
        const docUpdateResponse = await api.patch(`/workers/${workerId}`, updateData);
        
        console.log(`Document ${docType} successfully uploaded to cloud storage`);
        if (docUpdateResponse.status === 200) {
          // Update local state to reflect the change
          fetchWorkers();
          setSuccessMessage(`${docType.replace('_', ' ')} uploaded successfully`);
        }
      } catch (error: any) {
        console.error('Error uploading document:', error);
        setError(error.response?.data?.error || 'Failed to upload document');
      } finally {
        setLoading(false);
      }
    };

    input.click();
  };

  const handleViewDocument = async (docType: 'adhaar_doc' | 'pan_doc' | 'bank_doc', workerId: number) => {
    try {
      // Find the worker and get the document URL
      const worker = workers.find(w => w.id === workerId);
      if (!worker) {
        throw new Error('Worker not found');
      }

      const documentUrl = worker[docType];
      if (!documentUrl) {
        throw new Error('Document not found');
      }

      // For cloud storage URLs, just open the URL in a new tab
      window.open(documentUrl, '_blank');
    } catch (error: any) {
      console.error('Error viewing document:', error);
      setError(error.message || 'Failed to view document');
    }
  };

  return (
    <Box sx={{ height: 600, width: '100%', p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h4">Workers</Typography>
        <Button variant="contained" color="primary" onClick={handleOpen}>
          Add Worker
        </Button>
      </Box>

      <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search workers..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
        <FormControlLabel
          control={
            <Switch
              checked={showMissingDocs}
              onChange={(e) => setShowMissingDocs(e.target.checked)}
              color="primary"
            />
          }
          label="Show Missing Docs"
          sx={{ whiteSpace: 'nowrap' }}
        />
      </Box>

      <DataGrid
        rows={filteredWorkers}
        columns={columns}
        loading={loading}
        pageSizeOptions={[5, 10, 25]}
        initialState={{
          pagination: { paginationModel: { pageSize: 10 } },
        }}
      />

      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>{editMode ? 'Edit Worker' : 'Add New Worker'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              label="Worker ID"
              name="worker_id"
              value={formData.worker_id}
              onChange={handleInputChange}
              required
            />
            <TextField
              fullWidth
              label="Name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              required
            />
            <FormControl fullWidth required>
              <InputLabel>Worker Type</InputLabel>
              <Select
                name="worker_type"
                value={formData.worker_type}
                onChange={handleInputChange}
                label="Worker Type"
              >
                <MenuItem value="STITCHING">Stitching</MenuItem>
                <MenuItem value="PRESS">Press</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth required>
              <InputLabel>Status</InputLabel>
              <Select
                name="status"
                value={formData.status}
                onChange={handleInputChange}
                label="Status"
              >
                <MenuItem value="ACTIVE">Active</MenuItem>
                <MenuItem value="INACTIVE">Inactive</MenuItem>
              </Select>
            </FormControl>
            <TextField
              name="joining_date"
              label="Joining Date"
              type="date"
              value={formData.joining_date}
              onChange={handleInputChange}
              InputLabelProps={{ shrink: true }}
              required
            />
            <TextField
              name="phone"
              label="Phone Number"
              value={formData.phone}
              onChange={handleInputChange}
              required
            />
            <TextField
              name="address"
              label="Address"
              value={formData.address}
              onChange={handleInputChange}
              multiline
              rows={2}
              required
            />
            <TextField
              name="rate"
              label="Rate"
              value={formData.rate}
              onChange={handleInputChange}
              type="number"
              InputProps={{
                startAdornment: <InputAdornment position="start">₹</InputAdornment>,
              }}
              helperText="Worker's daily/piece rate"
            />
            <TextField
              name="adhaar_no"
              label="Aadhaar Number"
              value={formData.adhaar_no}
              onChange={handleInputChange}
              required
            />
            <TextField
              name="pan"
              label="PAN"
              value={formData.pan}
              onChange={handleInputChange}
              required
            />
            <TextField
              name="bank_account_name"
              label="Bank Account Name"
              value={formData.bank_account_name}
              onChange={handleInputChange}
              required
            />
            <TextField
              name="bank_account_no"
              label="Bank Account Number"
              value={formData.bank_account_no}
              onChange={handleInputChange}
              required
            />
            <TextField
              name="ifsc"
              label="IFSC"
              value={formData.ifsc}
              onChange={handleInputChange}
              required
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" color="primary">
            {editMode ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar 
        open={!!error} 
        autoHideDuration={6000} 
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setError(null)} severity="error">
          {error}
        </Alert>
      </Snackbar>

      {/* Success Message Snackbar */}
      <Snackbar 
        open={!!successMessage} 
        autoHideDuration={6000} 
        onClose={() => setSuccessMessage(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSuccessMessage(null)} severity="success">
          {successMessage}
        </Alert>
      </Snackbar>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this worker? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
} 